import { RouteRecordRaw, Router } from 'vue-router';

import { appRoutes } from '@/app/routes';
import Marketing from '@/marketing/views/MarketingView.vue';
import PhilosophyView from '@/marketing/views/PhilosophyView.vue';
import PricingView from '@/marketing/views/PricingView.vue';
import { useAppStore } from '@/shared/store';

declare module 'vue-router' {
    // SEO metadata for prerendered pages; applied once via useHead in App.vue.
    interface RouteMeta {
        title?: string;
        description?: string;
    }
}

export const routes: Readonly<RouteRecordRaw[]> = [
    {
        path: '/',
        name: 'marketing',
        component: Marketing,
        meta: {
            layout: 'marketing',
            title: 'Studoku — Effortless Scheduling',
            description:
                'Studoku builds studio lesson schedules in minutes, finding ' +
                'times that work for you and your students. Set availability ' +
                'and preferences; the solver does the rest.',
        },
    },
    {
        path: '/philosophy',
        name: 'philosophy',
        component: PhilosophyView,
        meta: {
            layout: 'marketing',
            title: 'Our Philosophy — Studoku',
            description:
                'Why Studoku rebuilds studio schedules from scratch each ' +
                'term, giving every student a fair shot at their ideal lesson ' +
                'time instead of locking in winners and losers.',
        },
    },
    {
        path: '/pricing',
        name: 'pricing',
        component: PricingView,
        meta: {
            layout: 'marketing',
            title: 'Pricing — Studoku',
            description:
                'Simple, capacity-based pricing: pay only for the students ' +
                'you teach. Start with a 30-day free trial, then pick the ' +
                'plan that fits your studio size.',
        },
        beforeEnter: async (_to, _from, next) => {
            // Prerender the page; the flag only gates client navigation.
            if (import.meta.env.SSR) {
                next();
                return;
            }
            const store = useAppStore();
            // Load features here since the flag decides the redirect below.
            await store.fetchFeatures();
            if (store.hasFeature('self-signup')) {
                next();
            } else {
                next({ name: 'marketing' });
            }
        },
    },
    {
        path: '/privacy-policy',
        name: 'privacy-policy',
        component: () => import('@/marketing/views/PrivacyPolicyView.vue'),
        meta: {
            layout: 'marketing',
            title: 'Privacy Policy — Studoku',
            description:
                'How Studoku collects, uses, and protects the personal ' +
                'information of teachers and their students.',
        },
    },
    {
        path: '/terms-of-service',
        name: 'terms-of-service',
        component: () => import('@/marketing/views/TermsOfServiceView.vue'),
        meta: {
            layout: 'marketing',
            title: 'Terms of Service — Studoku',
            description:
                'The terms and conditions governing your use of Studoku, ' +
                'the studio scheduling app.',
        },
    },
    {
        path: '/app',
        component: () => import('@/app/TeacherAppLayout.vue'),
        meta: {
            requiresAuth: true,
        },
        children: appRoutes as RouteRecordRaw[],
    },
    {
        path: '/survey/:tokenId',
        name: 'student-survey',
        component: () => import('@/app/views/StudentSurveyView.vue'),
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@/auth/views/LoginView.vue'),
        meta: {
            layout: 'auth',
        },
        beforeEnter: async (to, from, next) => {
            const store = useAppStore();
            if (store.authenticated) {
                const redirect = to.query['redirect'];
                if (redirect) {
                    next({ path: redirect.toString() });
                } else {
                    next({ name: 'app-index' });
                }
            } else {
                next();
            }
        },
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => import('@/auth/views/SignupEmailView.vue'),
        meta: {
            layout: 'auth',
        },
        beforeEnter: async (to, from, next) => {
            const store = useAppStore();
            if (store.authenticated) {
                next({ name: 'app-index' });
            } else {
                next();
            }
        },
    },
    {
        path: '/signup/complete',
        name: 'signup-complete',
        component: () => import('@/auth/views/SignupView.vue'),
        meta: {
            layout: 'auth',
        },
        beforeEnter: async (to, from, next) => {
            const store = useAppStore();
            if (store.authenticated) {
                next({ name: 'app-index' });
            } else {
                next();
            }
        },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/auth/views/PasswordResetRequestView.vue'),
        meta: {
            layout: 'auth',
        },
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('@/auth/views/PasswordResetFormView.vue'),
        meta: {
            layout: 'auth',
        },
    },
    {
        path: '/verify-email-change',
        name: 'verify-email-change',
        component: () => import('@/auth/views/EmailChangeVerificationView.vue'),
        meta: {
            layout: 'auth',
        },
    },

    {
        path: '/home',
        redirect: '/app/home',
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/auth/views/NotFoundView.vue'),
        meta: {
            layout: 'auth',
        },
    },
];

export function installRouterGuards(router: Router): void {
    router.beforeEach(async (to, from, next) => {
        // No session or flags to resolve when prerendering.
        if (import.meta.env.SSR) {
            next();
            return;
        }
        const store = useAppStore();
        if (to.matched.some((record) => record.meta.requiresAuth)) {
            if (!store.authenticated) {
                try {
                    await store.checkAuth();
                    next();
                } catch {
                    next({ name: 'login', query: { redirect: to.fullPath } });
                }
            } else {
                next();
            }
        } else if (to.meta.layout === 'marketing') {
            // Don't block first paint: fetch in the background and let views
            // reconcile reactively when features/auth resolve.
            void store.fetchFeatures();
            void store.checkAuth().catch(() => {});
            next();
        } else {
            try {
                await Promise.all([
                    store.fetchFeatures(),
                    store.checkAuth().catch(() => {}),
                ]);
            } finally {
                next();
            }
        }
    });
}
